import * as React from 'react'
import {
    apiCallWrapperNoDispatch,
    DataTable,
    DataTableColumnModel,
    DataTableModel,
    FilterType,
} from 'traveldesk-ui'
import { MetaDataModel } from '../models/MetaDataModel'

interface Props {
    apiPath: string
    data: MetaDataModel[]
    entityId: number
    nameLabel: string
    localize: (key: string) => string
    onTranslationChanged: (translation: MetaDataModel) => void
}

const BasicLanguageId = 1

const TranslationsTable = (props: Props) => {
    const [rows, setRows] = React.useState<MetaDataModel[]>([])
    const [dirtyLanguageIds, setDirtyLanguageIds] = React.useState<number[]>([])
    const [savingLanguageId, setSavingLanguageId] = React.useState<number | undefined>()

    React.useEffect(() => {
        setRows(props.data.map((x) => Object.assign(new MetaDataModel(), x)))
        setDirtyLanguageIds([])
    }, [props.data])

    const replaceRow = (translation: MetaDataModel, notifyParent: boolean) => {
        const current = rows.find((x) => x.languageId == translation.languageId)
        const updated = Object.assign(new MetaDataModel(), translation, {
            languageName: translation.languageName || current?.languageName || '',
        })
        setRows((items) => items.map((x) => (x.languageId == updated.languageId ? updated : x)))
        setDirtyLanguageIds((items) => items.filter((x) => x != updated.languageId))
        if (notifyParent) props.onTranslationChanged(updated)
    }

    const updateField = (translation: MetaDataModel, field: keyof MetaDataModel, value: string) => {
        const updated = Object.assign(new MetaDataModel(), translation, { [field]: value })
        setRows((items) => items.map((x) => (x.languageId == updated.languageId ? updated : x)))
        setDirtyLanguageIds((items) =>
            items.indexOf(updated.languageId) >= 0 ? items : items.concat(updated.languageId),
        )
    }

    const saveManualTranslation = async (translation: MetaDataModel) => {
        setSavingLanguageId(translation.languageId)
        const response = await apiCallWrapperNoDispatch<MetaDataModel>(
            `${props.apiPath}/translations/manual`,
            'POST',
            Object.assign(new MetaDataModel(), translation, { isManualTranslation: true }),
        )
        setSavingLanguageId(undefined)
        if (response.IsOk && response.Content) {
            replaceRow(response.Content, true)
        } else {
            alert(`${props.localize('Could not save translation')}: ${response.ErrorMessage || ''}`)
        }
    }

    const setManualTranslation = async (translation: MetaDataModel, isManual: boolean) => {
        if (!isManual) {
            const confirmed = confirm(
                props.localize('Your manual translation will be lost and replaced by an automatic translation. Continue?'),
            )
            if (!confirmed) return
        }

        if (isManual) {
            await saveManualTranslation(Object.assign(new MetaDataModel(), translation, { isManualTranslation: true }))
            return
        }

        setSavingLanguageId(translation.languageId)
        const response = await apiCallWrapperNoDispatch<MetaDataModel>(
            `${props.apiPath}/translations/${translation.languageId}/automatic`,
            'POST',
        )
        setSavingLanguageId(undefined)
        if (response.IsOk && response.Content) {
            replaceRow(response.Content, true)
        } else {
            alert(`${props.localize('Could not reset translation')}: ${response.ErrorMessage || ''}`)
        }
    }

    const renderTextInput = (translation: MetaDataModel, field: keyof MetaDataModel) =>
        translation.isManualTranslation ? (
            <input
                type="text"
                value={(translation[field] as string) || ''}
                onChange={(e) => updateField(translation, field, e.target.value)}
                style={{ width: '100%', minWidth: 140 }}
            />
        ) : (
            (translation[field] as string) || ''
        )

    const renderTextArea = (translation: MetaDataModel, field: keyof MetaDataModel) =>
        translation.isManualTranslation ? (
            <textarea
                value={(translation[field] as string) || ''}
                onChange={(e) => updateField(translation, field, e.target.value)}
                rows={3}
                style={{ width: '100%', minWidth: 180 }}
            />
        ) : (
            (translation[field] as string) || ''
        )

    const columns = [
        new DataTableColumnModel({
            fieldName: 'languageName',
            displayName: props.localize('Language'),
            sortable: true,
            filterType: FilterType.TEXT,
        }),
        new DataTableColumnModel({
            fieldName: 'isManualTranslation',
            displayName: props.localize('Manual Translation'),
            customRenderer: (translation: MetaDataModel) => (
                <input
                    type="checkbox"
                    checked={translation.isManualTranslation}
                    disabled={
                        props.entityId <= 0 ||
                        translation.languageId == BasicLanguageId ||
                        savingLanguageId == translation.languageId
                    }
                    onChange={(e) => setManualTranslation(translation, e.target.checked)}
                />
            ),
        }),
        new DataTableColumnModel({
            fieldName: 'name',
            displayName: props.nameLabel,
            customRenderer: (translation: MetaDataModel) => renderTextInput(translation, 'name'),
        }),
        new DataTableColumnModel({
            fieldName: 'pageTitle',
            displayName: props.localize('Page Title'),
            customRenderer: (translation: MetaDataModel) => renderTextInput(translation, 'pageTitle'),
        }),
        new DataTableColumnModel({
            fieldName: 'shortDescription',
            displayName: props.localize('Short Description'),
            customRenderer: (translation: MetaDataModel) => renderTextArea(translation, 'shortDescription'),
        }),
        new DataTableColumnModel({
            fieldName: 'description',
            displayName: props.localize('Description'),
            customRenderer: (translation: MetaDataModel) => renderTextArea(translation, 'description'),
        }),
        new DataTableColumnModel({
            fieldName: '',
            displayName: '',
            customRenderer: (translation: MetaDataModel) =>
                translation.isManualTranslation && translation.languageId != BasicLanguageId ? (
                    <button
                        type="button"
                        disabled={
                            dirtyLanguageIds.indexOf(translation.languageId) < 0 ||
                            savingLanguageId == translation.languageId
                        }
                        onClick={() => saveManualTranslation(translation)}
                    >
                        {savingLanguageId == translation.languageId
                            ? props.localize('Saving...')
                            : props.localize('Save')}
                    </button>
                ) : null,
        }),
    ]

    return (
        <DataTable
            data={rows}
            fixedHeight={300}
            dataTable={new DataTableModel({ name: props.localize('Translations'), columns })}
        />
    )
}

export default TranslationsTable
